<script setup lang="ts">
import config from "@/config";
import { useAppStore } from "@/store/app";
import { defineAsyncMicroApp, unmountApp } from "@gohighlevel/ghl-module-federation-plugin";
import { UISpin } from "@gohighlevel/ghl-ui";
import mitt from "mitt";
import { onBeforeUnmount, onMounted, ref, markRaw } from "vue";
import { useRoute } from "vue-router";
const appStore = useAppStore();
const route = useRoute();
// ---------------------------------------------------------------------------
// Module-federated import of the submissions page coming from form-survey app
// ---------------------------------------------------------------------------

/**
 * Utility that makes the remote application instance globally accessible.
 * Mirrors the logic used in `MediaPickerApp.vue`.
 */
const mountAppContext = (apps: Record<string, unknown>): void => {
  Object.keys(apps).forEach((key) => {
    (window as any)[key] = apps[key];
  });
};

// Reference to the dynamically created remote Vue application
const submissionsPageApp = ref<any>(null);
const submissionsPageProps = ref<any>({});

// We will lazy-load remote component to ensure `window.formApp` exists beforehand.

type RemoteComponent = any;
const remoteComponents: Record<string, RemoteComponent> = {};

// Will hold the createApp function provided by the remote container.
// eslint-disable-next-line @typescript-eslint/no-explicit-any

// Clean up any persisted Pinia sessionStorage entries from the remote app
// to avoid stale state affecting UI when opened from the builder.
const clearRemotePersistedState = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key) {
        continue;
      }
      // Likely keys used by pinia-plugin-persistedstate or known remote artifacts
      const matchesPinia = key === "pinia" || key.startsWith("pinia") || key.includes("pinia");
      const knownRemoteKeys = key === "store" || key === "selectedDatesFromAnalyze";
      if (matchesPinia || knownRemoteKeys) {
        keysToRemove.push(key);
        continue;
      }
      // Heuristic: JSON payload shaped like the remote submissions store
      try {
        const raw = sessionStorage.getItem(key);
        if (!raw) {
          continue;
        }
        const parsed = JSON.parse(raw);
        if (
          parsed &&
          typeof parsed === "object" &&
          (Object.prototype.hasOwnProperty.call(parsed, "filters") ||
            Object.prototype.hasOwnProperty.call(parsed, "selectedDates")) &&
          Object.prototype.hasOwnProperty.call(parsed, "submissions")
        ) {
          keysToRemove.push(key);
        }
      } catch (e) {
        // not JSON or unexpected format; ignore
      }
    }
    keysToRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("Failed to clear remote persisted state", err);
  }
};

// Default route with params & query expected by SubmissionsPage

// Store structure for the remote component
const store = {
  state: {
    user: {
      user: { role: "admin" },
    },
    // Add locations module with current location
    locations: {
      currentLocation: {
        _id: appStore.locationId,
        timezone: appStore.timezone || "UTC", // Fallback to UTC, if the timezone is not set
      },
    },
    // Add company module
    company: {
      company: {
        id: appStore.company?.id || "",
        name: appStore.company?.name || "",
      },
    },
    // Provide integrate module with baseUrl derived from config
    integrate: {
      baseUrl: `${config.mode === "dev" ? "http" : "https"}://${
        appStore.company.domain || config.GHL_DOMAIN
      }`,
    },
  },
  // Add getters that remote component might expect
  getters: {
    "sidebarv2/getVersion": () => "v2", // Default sidebar version
    "user/get": () => store.state.user?.user || { role: "admin" },
  },
  // Add dispatch method for handling actions
  dispatch: async (action: string, payload?: any) => {
    // Handle locationCustomFields module actions
    if (action === "locationCustomFields/syncAll") {
      // Load custom fields if not already loaded
      if (!appStore.customFields || appStore.customFields.length === 0) {
        await appStore.getCustomFields();
        await appStore.getCustomFieldFolders();
      }
      return;
    }

    if (action === "locationCustomFields/getCustomFields") {
      return appStore.customFields || [];
    }

    return null;
  },
};

// Loading state for the remote component
const isLoading = ref<boolean>(true);

// Mount function
const mountSubmissionsPage = async (): Promise<void> => {
  try {
    isLoading.value = true;
    submissionsPageApp.value = null;
    submissionsPageProps.value = {};

    // Ensure no stale sessionStorage from remote Pinia persists between mounts
    clearRemotePersistedState();

    // Ensure a stub app is present before evaluating remote module
    // so that setupPinia and setupVueI18n don't throw.
    (window as any).formApp = { use: () => {} };

    // Provide a fallback getToken implementation if host hasn't defined one.
    if (typeof (window as any).getToken !== "function") {
      (window as any).getToken = async (): Promise<string> => {
        return appStore?.tokenId || "";
      };
    }

    const product = appStore.product || "form";

    // Ensure the correct submissions component is loaded
    if (!remoteComponents[product]) {
      const productMap: Record<string, string> = {
        form: "FormSubmissions",
        survey: "SurveySubmissions",
        quiz: "QuizSubmissions",
      };

      const moduleName = productMap[product];
      if (!moduleName) {
        throw new Error(`Unsupported product type: ${product}`);
      }
      const [componentMod] = await defineAsyncMicroApp("formSurveyApp", [moduleName]);
      remoteComponents[product] = componentMod;
    }

    route.params.location_id = appStore.locationId;
    submissionsPageProps.value = {
      store,
      emitter: mitt(),
      locale: localStorage.getItem("locale") || "en-US",
      entityType: product.toUpperCase(),
      ...(product === "form" && appStore.formId ? { entityId: appStore.formId } : {}),
      ...(product === "survey" && appStore.surveyId ? { entityId: appStore.surveyId } : {}),
      ...(product === "quiz" && appStore.quizId ? { entityId: appStore.quizId } : {}),
    };
    submissionsPageApp.value = markRaw(remoteComponents[product]);
    // Replace stub with real app instance for plugins registered inside remote
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // Set loading to false after successful mount
    isLoading.value = false;
  } catch (error) {
    console.error("Failed to mount submissions page:", error);
    isLoading.value = false;
  }
};

// Lifecycle hooks
onMounted(() => {
  mountSubmissionsPage();
});

onBeforeUnmount(() => {
  submissionsPageApp.value = null;
  submissionsPageProps.value = {};
  unmountApp("formSurveyApp");
});
</script>

<template>
  <!-- Container into which the remote SubmissionsPage app will mount -->
  <div class="relative h-full w-full">
    <!-- Loading spinner -->
    <div v-if="isLoading" class="flex min-h-screen -translate-y-32 items-center justify-center">
      <UISpin size="large" />
    </div>

    <!-- Remote app container -->
    <div id="fsb-submissions-page" class="h-full w-full">
      <component :is="submissionsPageApp" v-bind="submissionsPageProps" v-if="submissionsPageApp" />
    </div>
  </div>
</template>

<style>
#fsb-submissions-page {
  width: 100%;
  height: 100%;
}
#fsb-submissions-page button {
  border: 0;
}
</style>
